import * as React from 'react';
import {
  screenXs,
  screenSm,
  screenSmMin,
  screenMd,
  screenMdMin,
  screenLg,
  screenLgMin,
  screenXl,
  screenXlMin,
} from '@spotify-internal/encore-foundation';

export enum Screen {
  XS = parseInt(screenXs, 10),
  SM = parseInt(screenSm, 10),
  MD = parseInt(screenMd, 10),
  LG = parseInt(screenLg, 10),
  XL = parseInt(screenXl, 10),
}

export enum Viewport {
  XS = 'XS',
  SM = 'SM',
  MD = 'MD',
  LG = 'LG',
  XL = 'XL',
}

export const breakpointValues = {
  [Viewport.XS]: Screen.XS,
  [Viewport.SM]: Screen.SM,
  [Viewport.MD]: Screen.MD,
  [Viewport.LG]: Screen.LG,
  [Viewport.XL]: Screen.XL,
};

const smQuery = window.matchMedia(`(min-width: ${screenSmMin})`);
const mdQuery = window.matchMedia(`(min-width: ${screenMdMin})`);
const lgQuery = window.matchMedia(`(min-width: ${screenLgMin})`);
const xlQuery = window.matchMedia(`(min-width: ${screenXlMin})`);

const defaultQueries: Queries = {
  smQuery,
  mdQuery,
  lgQuery,
  xlQuery,
};

type Queries = {
  smQuery: MediaQueryList;
  mdQuery: MediaQueryList;
  lgQuery: MediaQueryList;
  xlQuery: MediaQueryList;
};

// @visibleForTesting
export function getMatch(queries: Queries) {
  let v = Viewport.XS;
  if (queries.smQuery.matches) v = Viewport.SM;
  if (queries.mdQuery.matches) v = Viewport.MD;
  if (queries.lgQuery.matches) v = Viewport.LG;
  if (queries.xlQuery.matches) v = Viewport.XL;
  return v;
}

export const useBreakpointValue = () => {
  const vp = useViewport();
  return breakpointValues[vp];
};

export const useViewport: (queries?: Queries) => Viewport = (
  queries = defaultQueries,
) => {
  const [viewport, setViewport] = React.useState(() => getMatch(queries));
  const handleResize = () => setViewport(getMatch(queries));

  React.useEffect(() => {
    handleResize();
    queries.smQuery.addListener(handleResize);
    queries.mdQuery.addListener(handleResize);
    queries.lgQuery.addListener(handleResize);
    queries.xlQuery.addListener(handleResize);
    return () => {
      queries.smQuery.removeListener(handleResize);
      queries.mdQuery.removeListener(handleResize);
      queries.lgQuery.removeListener(handleResize);
      queries.xlQuery.removeListener(handleResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return viewport;
};
